/**
 * HomePage - Landing page for Budgira
 *
 * Presents the product to visitors with:
 * - Hero section with main value proposition
 * - Features overview
 * - Pricing tiers (Free vs Premium) with dynamic prices from PayPal config
 * - Call to action
 *
 * @module pages/HomePage
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageSelector } from '../components/common/LanguageSelector';
import { publicConfigService, type PublicConfig } from '../services/public-config.service';

const HomePage = () => {
  const { t } = useTranslation();
  const [config, setConfig] = useState<PublicConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load public config on mount
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const response = await publicConfigService.get();
        if (response.success && response.data) {
          setConfig(response.data);
        }
      } catch (err) {
        console.error('Failed to load config:', err);
      } finally {
        setIsLoading(false);
      }
    };
    loadConfig();
  }, []);

  // Get values from config with defaults
  const operationLimit = config?.freeAccount.operationLimit ?? 50;
  const accountLimit = config?.freeAccount.accountLimit ?? 1;
  const monthlyPrice = config?.subscription.monthlyPrice ?? 0.49;
  const annualMonthlyEquivalent = config?.subscription.annualMonthlyEquivalent ?? 0.35;
  const annualSavingsPercent = config?.subscription.annualSavingsPercent ?? 29;

  // Format price for display
  const formatPrice = (price: number) => {
    return price.toFixed(2).replace('.', ',');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-slate-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-slate-200 dark:border-slate-700">
        <div className="container mx-auto px-3 sm:px-4 py-3 sm:py-4">
          {/* Mobile-optimized layout */}
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-1.5 sm:gap-2 text-lg sm:text-2xl font-display font-bold text-violet-600 flex-shrink-0">
              <img src="/budgira.svg" alt="Budgira" className="w-6 h-6 sm:w-8 sm:h-8" />
              <span>Budgira</span>
            </Link>
            <div className="flex items-center gap-2 sm:gap-4">
              <LanguageSelector variant="compact" className="bg-slate-100 dark:bg-slate-700" />
              <Link
                to="/auth/login"
                className="text-xs sm:text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-violet-600 dark:hover:text-violet-400 transition-colors whitespace-nowrap"
              >
                {t('home.header.login')}
              </Link>
              <Link
                to="/auth/register"
                className="hidden sm:inline-block px-4 py-2 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 rounded-lg transition-colors whitespace-nowrap"
              >
                {t('home.header.register')}
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-16 md:py-24 text-center">
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-slate-900 dark:text-white mb-6">
          {t('home.hero.title')}
        </h1>
        <p className="text-lg md:text-xl text-slate-600 dark:text-slate-300 max-w-2xl mx-auto mb-8">
          {t('home.hero.subtitle')}
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/auth/register"
            className="px-8 py-3 text-lg font-medium text-white bg-violet-600 hover:bg-violet-700 rounded-xl shadow-lg shadow-violet-200 dark:shadow-violet-900/30 transition-all hover:scale-105"
          >
            {t('home.hero.cta')}
          </Link>
          <Link
            to="/auth/login"
            className="px-8 py-3 text-lg font-medium text-violet-600 dark:text-violet-400 bg-white dark:bg-slate-800 border-2 border-violet-200 dark:border-violet-700 hover:border-violet-400 dark:hover:border-violet-500 rounded-xl transition-all"
          >
            {t('home.hero.login')}
          </Link>
        </div>
      </section>

      {/* Features Section */}
      <section className="container mx-auto px-4 py-16 md:py-24">
        <h2 className="text-3xl md:text-4xl font-display font-bold text-slate-900 dark:text-white text-center mb-4">
          {t('home.features.title')}
        </h2>
        <p className="text-slate-600 dark:text-slate-300 text-center max-w-2xl mx-auto mb-12">
          {t('home.features.subtitle')}
        </p>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Feature 1 - Multi-accounts */}
          <FeatureCard
            icon={<BankIcon />}
            title={t('home.features.accounts.title')}
            description={t('home.features.accounts.description')}
          />

          {/* Feature 2 - Operations */}
          <FeatureCard
            icon={<ListIcon />}
            title={t('home.features.operations.title')}
            description={t('home.features.operations.description')}
          />

          {/* Feature 3 - Categories */}
          <FeatureCard
            icon={<TagIcon />}
            title={t('home.features.categories.title')}
            description={t('home.features.categories.description')}
          />

          {/* Feature 4 - Schedulers */}
          <FeatureCard
            icon={<CalendarIcon />}
            title={t('home.features.schedulers.title')}
            description={t('home.features.schedulers.description')}
          />

          {/* Feature 5 - Dashboard */}
          <FeatureCard
            icon={<ChartIcon />}
            title={t('home.features.dashboard.title')}
            description={t('home.features.dashboard.description')}
          />

          {/* Feature 6 - Security */}
          <FeatureCard
            icon={<ShieldIcon />}
            title={t('home.features.security.title')}
            description={t('home.features.security.description')}
          />
        </div>
      </section>

      {/* Pricing Section */}
      <section className="bg-slate-50 dark:bg-slate-800/50 py-16 md:py-24">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-slate-900 dark:text-white text-center mb-4">
            {t('home.pricing.title')}
          </h2>
          <p className="text-slate-600 dark:text-slate-300 text-center max-w-2xl mx-auto mb-12">
            {t('home.pricing.subtitle')}
          </p>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Free Plan */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl p-8 border border-slate-200 dark:border-slate-700 shadow-sm">
              <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                {t('home.pricing.free.title')}
              </h3>
              <p className="text-slate-500 dark:text-slate-400 mb-6">
                {t('home.pricing.free.description')}
              </p>
              <div className="text-4xl font-bold text-slate-900 dark:text-white mb-6">
                {t('home.pricing.free.price')}
              </div>
              <ul className="space-y-3 mb-8">
                <PricingFeature included>
                  {isLoading ? t('home.pricing.free.features.accounts') : t('home.pricing.free.features.accountsDynamic', { count: accountLimit })}
                </PricingFeature>
                <PricingFeature included>
                  {isLoading ? t('home.pricing.free.features.operations') : t('home.pricing.free.features.operationsDynamic', { count: operationLimit })}
                </PricingFeature>
                <PricingFeature included>{t('home.pricing.free.features.dashboard')}</PricingFeature>
                <PricingFeature included>{t('home.pricing.free.features.categories')}</PricingFeature>
                <PricingFeature included>{t('home.pricing.free.features.paymentMethods')}</PricingFeature>
              </ul>
              <Link
                to="/auth/register"
                className="block w-full py-3 text-center font-medium text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-900/30 hover:bg-violet-100 dark:hover:bg-violet-900/50 rounded-xl transition-colors"
              >
                {t('home.pricing.free.cta')}
              </Link>
            </div>

            {/* Premium Plan */}
            <div className="bg-gradient-to-br from-violet-600 to-violet-700 rounded-2xl p-8 text-white shadow-xl shadow-violet-200 dark:shadow-violet-900/50 relative overflow-hidden">
              <div className="absolute top-4 right-4 px-3 py-1 bg-amber-400 text-amber-900 text-xs font-bold rounded-full">
                {t('home.pricing.premium.badge')}
              </div>
              <h3 className="text-2xl font-bold mb-2">
                {t('home.pricing.premium.title')}
              </h3>
              <p className="text-violet-200 mb-6">
                {t('home.pricing.premium.description')}
              </p>
              
              {/* Dynamic pricing display */}
              <div className="mb-6">
                {isLoading ? (
                  <div className="h-12 bg-violet-500/50 rounded animate-pulse" />
                ) : (
                  <>
                    <div className="text-4xl font-bold">
                      {formatPrice(annualMonthlyEquivalent)} € <span className="text-lg font-normal text-violet-200">/ {t('home.pricing.premium.perMonth')}</span>
                    </div>
                    <div className="text-violet-200 text-sm mt-1">
                      {t('home.pricing.premium.annualBilling')}
                    </div>
                    <div className="mt-2 inline-block bg-green-500/20 text-green-200 text-xs font-medium px-2 py-1 rounded-full">
                      {t('home.pricing.premium.savingsPercent', { percent: annualSavingsPercent })}
                    </div>
                    <div className="text-violet-300 text-sm mt-2">
                      {t('home.pricing.premium.orMonthly', { price: formatPrice(monthlyPrice) })}
                    </div>
                  </>
                )}
              </div>

              <ul className="space-y-3 mb-8">
                <PricingFeature included light>{t('home.pricing.premium.features.accounts')}</PricingFeature>
                <PricingFeature included light>{t('home.pricing.premium.features.operations')}</PricingFeature>
                <PricingFeature included light>{t('home.pricing.premium.features.dashboard')}</PricingFeature>
                <PricingFeature included light>{t('home.pricing.premium.features.categories')}</PricingFeature>
                <PricingFeature included light>{t('home.pricing.premium.features.paymentMethods')}</PricingFeature>
                <PricingFeature included light>{t('home.pricing.premium.features.schedulers')}</PricingFeature>
                <PricingFeature included light>{t('home.pricing.premium.features.import')}</PricingFeature>
                <PricingFeature included light>{t('home.pricing.premium.features.sharedAccess')}</PricingFeature>
              </ul>
              <Link
                to="/premium"
                className="block w-full py-3 text-center font-medium text-violet-700 bg-white hover:bg-violet-50 rounded-xl transition-colors"
              >
                {t('home.pricing.premium.cta')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-4 py-16 md:py-24 text-center">
        <h2 className="text-3xl md:text-4xl font-display font-bold text-slate-900 dark:text-white mb-4">
          {t('home.cta.title')}
        </h2>
        <p className="text-slate-600 dark:text-slate-300 max-w-xl mx-auto mb-8">
          {t('home.cta.subtitle')}
        </p>
        <Link
          to="/auth/register"
          className="inline-block px-8 py-3 text-lg font-medium text-white bg-violet-600 hover:bg-violet-700 rounded-xl shadow-lg shadow-violet-200 dark:shadow-violet-900/30 transition-all hover:scale-105"
        >
          {t('home.cta.button')}
        </Link>
      </section>

      {/* MP Soft Solutions Banner */}
      <section className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 py-12 md:py-16 relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-24 -left-24 w-96 h-96 bg-violet-500/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            {/* Text content */}
            <div className="text-center md:text-left max-w-xl">
              <div className="inline-flex items-center gap-2 bg-violet-500/20 text-violet-300 text-xs font-medium px-3 py-1 rounded-full mb-4">
                <SparkleIcon className="w-4 h-4" />
                {t('home.mpsoft.badge', 'Développé par')}
              </div>
              <h3 className="text-2xl md:text-3xl font-display font-bold text-white mb-3">
                {t('home.mpsoft.title', 'MP Soft Solutions')}
              </h3>
              <p className="text-slate-300 mb-6">
                {t('home.mpsoft.description', 'Besoin d\'un logiciel sur mesure, d\'une application web ou mobile ? Notre équipe transforme vos idées en solutions numériques performantes.')}
              </p>
              <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                <a
                  href="https://www.mpsoftsolutions.com/#features"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-500 hover:to-blue-500 text-white font-medium rounded-xl shadow-lg shadow-violet-500/25 transition-all hover:scale-105"
                >
                  {t('home.mpsoft.cta', 'Découvrir nos services')}
                  <ArrowRightIcon className="w-4 h-4" />
                </a>
                <a
                  href="https://www.mpsoftsolutions.com/#products"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl border border-white/20 transition-all hover:scale-105"
                >
                  {t('home.mpsoft.ctaApps', 'Découvrir nos applications')}
                  <ArrowRightIcon className="w-4 h-4" />
                </a>
              </div>
            </div>
            
            {/* Services highlights */}
            <div className="grid grid-cols-2 gap-4 text-center">
              <ServiceBadge icon={<CodeIcon />} label={t('home.mpsoft.services.web', 'Applications Web')} />
              <ServiceBadge icon={<CogIcon />} label={t('home.mpsoft.services.custom', 'Sur Mesure')} />
              <ServiceBadge icon={<SparkleIcon className="w-6 h-6" />} label={t('home.mpsoft.services.modern', 'Moderne')} />
              <ServiceBadge icon={<GlobeIcon />} label={t('home.mpsoft.services.websites', 'Sites Web')} />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start gap-2">
                <img src="/budgira.svg" alt="Budgira" className="w-8 h-8" />
                <span className="text-2xl font-display font-bold text-white">Budgira</span>
              </div>
              <p className="text-sm mt-1">{t('home.footer.tagline')}</p>
            </div>
            <nav className="flex flex-wrap justify-center gap-6 text-sm">
              <Link to="/about" className="hover:text-white transition-colors">
                {t('home.footer.about')}
              </Link>
              <Link to="/contact" className="hover:text-white transition-colors">
                {t('home.footer.contact')}
              </Link>
              <Link to="/privacy" className="hover:text-white transition-colors">
                {t('home.footer.privacy')}
              </Link>
              <Link to="/terms" className="hover:text-white transition-colors">
                {t('home.footer.terms')}
              </Link>
            </nav>
          </div>
          <div className="mt-8 pt-8 border-t border-slate-800 text-center text-sm">
            {t('home.footer.copyright', { year: new Date().getFullYear() })}
          </div>
        </div>
      </footer>
    </div>
  );
};

// Feature Card Component
interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const FeatureCard = ({ icon, title, description }: FeatureCardProps) => (
  <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md transition-shadow">
    <div className="w-12 h-12 bg-violet-100 dark:bg-violet-900/30 rounded-xl flex items-center justify-center text-violet-600 dark:text-violet-400 mb-4">
      {icon}
    </div>
    <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">{title}</h3>
    <p className="text-slate-600 dark:text-slate-400">{description}</p>
  </div>
);

// Pricing Feature Component
interface PricingFeatureProps {
  children: React.ReactNode;
  included?: boolean;
  light?: boolean;
}

const PricingFeature = ({ children, included = false, light = false }: PricingFeatureProps) => (
  <li className="flex items-center gap-3">
    {included ? (
      <CheckIcon className={light ? 'text-violet-200' : 'text-green-500'} />
    ) : (
      <XIcon className="text-slate-300 dark:text-slate-600" />
    )}
    <span className={included ? '' : 'text-slate-400 dark:text-slate-500 line-through'}>
      {children}
    </span>
  </li>
);

// Icons
const BankIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
  </svg>
);

const ListIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
  </svg>
);

const TagIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
  </svg>
);

const CalendarIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const ChartIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);

const ShieldIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

const CheckIcon = ({ className }: { className?: string }) => (
  <svg className={`w-5 h-5 ${className}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const XIcon = ({ className }: { className?: string }) => (
  <svg className={`w-5 h-5 ${className}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

// MP Soft Solutions Section Components
interface ServiceBadgeProps {
  icon: React.ReactNode;
  label: string;
}

const ServiceBadge = ({ icon, label }: ServiceBadgeProps) => (
  <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 hover:bg-white/10 transition-colors">
    <div className="text-violet-400 mb-2 flex justify-center">
      {icon}
    </div>
    <div className="text-white text-sm font-medium">{label}</div>
  </div>
);

const SparkleIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
  </svg>
);

const ArrowRightIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
  </svg>
);

const CodeIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
  </svg>
);

const GlobeIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
  </svg>
);

const CogIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

export default HomePage;
